import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';

// Tipo de respuesta esperada del refresh
interface RefreshResponse {
  access_token: string;
}

// Fila en la cola de peticiones en espera del refresh
interface FailedRequest {
  resolve: (value: string) => void;
  reject: (error: FetchBaseQueryError) => void;
}

let isRefreshing = false;
let failedQueue: FailedRequest[] = [];
let lastSettingsVersion: string | null = null;

const PRIVATE_PREFIXES = ["auth/", "/api/auth/"];
const SETTINGS_PATH_MATCHER = "systems-settings";
const MAINTENANCE_ROUTE = "/maintenance";
const MAINTENANCE_MESSAGE_KEY = "maintenance_message";
const MAINTENANCE_CODE = "MAINTENANCE_MODE";

const getRequestUrl = (args: string | FetchArgs): string => {
  if (typeof args === "string") return args;
  return args.url ?? "";
};

const isPrivateRequest = (args: string | FetchArgs): boolean => {
  const normalized = getRequestUrl(args).toLowerCase();
  return PRIVATE_PREFIXES.some((prefix) => normalized.includes(prefix.toLowerCase()));
};

const isSystemSettingsRequest = (args: string | FetchArgs): boolean => {
  return getRequestUrl(args).toLowerCase().includes(SETTINGS_PATH_MATCHER);
};

const readSettingsVersionHeader = (result: { meta?: unknown }): string | null => {
  const meta = result.meta as { response?: Response } | undefined;
  const headerValue = meta?.response?.headers?.get('X-System-Settings-Version');
  if (!headerValue || headerValue.trim().length === 0) {
    return null;
  }
  return headerValue;
};

const syncSystemSettingsVersion = (
  result: { meta?: unknown },
  invalidate: () => void
) => {
  const headerVersion = readSettingsVersionHeader(result);
  if (!headerVersion) return;

  if (lastSettingsVersion === null) {
    lastSettingsVersion = headerVersion;
    return;
  }

  if (headerVersion !== lastSettingsVersion) {
    lastSettingsVersion = headerVersion;
    invalidate();
  }
};

const processQueue = (error: FetchBaseQueryError | null, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) {
      resolve(token);
    } else if (error){
      reject(error);
    }
  });
  failedQueue = [];
};


const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL,
  credentials: 'include', // Necesario si el refresh usa cookies HttpOnly
  prepareHeaders: (headers) => {
    const token = sessionStorage.getItem('access_token');
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

const handleMaintenanceResponse = (result: { error?: FetchBaseQueryError }) => {
  if (!result.error || result.error.status !== 503) return false;

  const data = result.error.data as { code?: string; message?: string } | undefined;
  if (data?.code !== MAINTENANCE_CODE) return false;

  const message = data.message ?? "Sistema en mantenimiento. Por favor, intente más tarde.";
  sessionStorage.setItem(MAINTENANCE_MESSAGE_KEY, message);

  if (window.location.pathname !== MAINTENANCE_ROUTE) {
    window.location.href = MAINTENANCE_ROUTE;
  }

  return true;
};

export const customBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  let result = await baseQuery(args, api, extraOptions);

  if (handleMaintenanceResponse({ error: result.error })) {
    return result;
  }

  // Primero permiso denegado
  if (result.error?.status === 403) {
    if (isPrivateRequest(args)) {
      syncSystemSettingsVersion(result, () => {
        if (!isSystemSettingsRequest(args)) {
          api.dispatch(apiSlice.util.invalidateTags(['SystemSettings']));
        }
      });
    }
    return result;
  }

  // Token inválido o expirado
  if (result.error && result.error.status === 401) {
    const originalRequest = args;

    if (!isRefreshing) {
      isRefreshing = true;
      try {

        const refreshResult = await baseQuery(
          { url: '/Session/resign-in', method: 'POST' },
          api,
          extraOptions
        );

        if (refreshResult.data) {
          syncSystemSettingsVersion(refreshResult, () => {
            api.dispatch(apiSlice.util.invalidateTags(['SystemSettings']));
          });

          const newToken = (refreshResult.data as RefreshResponse).access_token;
          sessionStorage.setItem('access_token', newToken);
          processQueue(null, newToken);

          // Reintentar petición original con nuevo token
          result = await baseQuery(originalRequest, api, extraOptions);
        } else {
          throw refreshResult.error ?? new Error('No se pudo refrescar el token');
        }
      } catch (error) {
         const typedError: FetchBaseQueryError = 
          error && typeof error === 'object' && 'status' in error
            ? (error as FetchBaseQueryError)
            : { 
                status: 'CUSTOM_ERROR', 
                data: 'Error desconocido en refresh',
                error: 'Refresh token error'
              };
        processQueue(typedError, null);
        sessionStorage.removeItem('access_token');
        window.location.href = '/auth';
        return { error: typedError };
      } finally {
        isRefreshing = false;
      }
    } else {
     // Espera a que termine el refresh actual
      try {
        await new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        });

        // retry con token ya guardado
        result = await baseQuery(originalRequest, api, extraOptions);
      } catch (e) {
        return { error: e as FetchBaseQueryError };
      }
    }
  }

  if (handleMaintenanceResponse({ error: result.error })) {
    return result;
  }

  if (isPrivateRequest(args)) {
    syncSystemSettingsVersion(result, () => {
      if (!isSystemSettingsRequest(args)) {
        api.dispatch(apiSlice.util.invalidateTags(['SystemSettings']));
      }
    });
  }

  return result;
};


export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: customBaseQuery,
  tagTypes: ['Auth', 'AccessControl', 'User', 'UserGroup', 'Company', 'Employee', 'Product', 'Catalogue', 
    'Location', 'LocationPlace', 'Movements', 'Vouchers', 'Suppliers' , 'ProductUbicationSettings',
    'InventoryReports', 'WorkOrders', 'Tasks', 'TasksData', 'Workspaces', 'RolesWorkspaces', 'WorkspaceCompanies', 'ExternalOt', 'ExternalOts',
    'Certifications', 'Certification', 'CertificationKinds', 'CertificationStates',
    'CertificationRecords', 'CertificationRecord', 'CertificationRecordSettings', 'EligibleCertifications', 'SystemSettings'],
  endpoints: () => ({}),
})
